/**
 * 通用滚动加载工具
 * 用于处理滚动到底部时自动加载更多数据的场景
 * 内置了默认的加载指示器和错误提示UI，无需额外配置
 */

import { pushErrorMessage } from '../components/jky-toast/init-toast';

class ScrollLoader {
  /**
   * 创建滚动加载实例
   * @param {Object} options 配置选项
   * @param {HTMLElement} options.container 滚动容器元素
   * @param {Function} options.loadData 加载数据的函数，必须返回Promise，且返回格式为 {list: Array, total: Number}
   * @param {Function} options.renderData 渲染数据的函数，接收参数 (items, total)
   * @param {Number} options.pageSize 每页数据条数，默认10
   * @param {Number} options.threshold 触发加载的阈值（距离底部多少像素时加载），默认100
   * @param {String} options.cacheKeyPrefix 缓存键前缀，默认'scroll_loader'
   * @param {Function} options.onError 错误处理函数，接收参数 (error)
   * @param {Object} [options.loadingUI] 加载UI配置（可选，组件已内置默认UI）
   * @param {Function} options.loadingUI.show 显示加载UI的函数
   * @param {Function} options.loadingUI.hide 隐藏加载UI的函数
   * @param {Object} [options.errorUI] 错误UI配置（可选，组件已内置默认UI）
   * @param {Function} options.errorUI.show 显示错误UI的函数，接收参数 (message)
   */
  constructor(options) {
    // 必需参数检查
    if (!options.container || !(options.container instanceof HTMLElement)) {
      throw new Error('ScrollLoader: container必须是一个有效的DOM元素');
    }
    if (typeof options.loadData !== 'function') {
      throw new Error('ScrollLoader: loadData必须是一个函数');
    }
    if (typeof options.renderData !== 'function') {
      throw new Error('ScrollLoader: renderData必须是一个函数');
    }

    // 容器元素
    this.container = options.container;

    // 数据加载和渲染函数
    this.loadData = options.loadData;
    this.renderData = options.renderData;

    // 配置参数
    this.config = {
      pageSize: options.pageSize || 10,
      threshold: options.threshold || 100,
      cacheKeyPrefix: options.cacheKeyPrefix || 'scroll_loader',
      ...options
    };

    // 滚动加载状态
    this.page = 1;
    this.isLoading = false;
    this.hasMore = true;

    // 数据缓存
    this.cache = new Map();

    // 初始化
    this.init();
  }

  /**
   * 初始化滚动加载
   */
  init() {
    // 确保容器可滚动
    this.ensureScrollableContainer();

    // 绑定滚动事件
    this.bindScrollEvent();

    // 自动加载第一页（默认行为）
    this.load();
  }

  /**
   * 确保容器可滚动
   */
  ensureScrollableContainer() {
    if (!this.container) return;

    const style = window.getComputedStyle(this.container);
    const hasScrollStyle = style.overflowY === 'scroll' || style.overflowY === 'auto';

    if (!hasScrollStyle) {
      this.container.style.overflowY = 'auto';

      // 如果没有设置最大高度，设置一个默认值
      if (style.maxHeight === 'none' || !style.maxHeight) {
        this.container.style.maxHeight = '400px';
      }
    }
  }

  /**
   * 绑定滚动事件
   */
  bindScrollEvent() {
    // 使用全局防抖函数包装 onScroll 方法
    this.onScroll = debounce(this.onScroll.bind(this), 100);

    // 检查容器是否可滚动
    const style = window.getComputedStyle(this.container);
    const hasScrollStyle = style.overflowY === 'scroll' || style.overflowY === 'auto';

    if (!hasScrollStyle) {
      console.warn('[ScrollLoader] 警告: 容器没有设置overflow-y:auto或scroll样式，可能无法触发滚动事件');
    }

    this.container.addEventListener('scroll', this.onScroll, { passive: true });
  }

  /**
   * 滚动事件处理
   */
  onScroll() {
    // 检查是否应该加载
    if (this.isLoading || !this.hasMore) {
      return;
    }

    // 获取滚动状态
    const { scrollTop, scrollHeight, clientHeight } = this.container;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;

    // 检查是否达到加载阈值
    if (scrollBottom <= this.config.threshold) {
      this.load();
    }
  }

  /**
   * 显示加载指示器
   * 内置默认实现，也支持通过 config.loadingUI 覆盖
   */
  showLoading() {
    // 如果提供了自定义加载UI，则使用自定义实现
    if (this.config.loadingUI && typeof this.config.loadingUI.show === 'function') {
      this.config.loadingUI.show();
      return;
    }

    // 默认加载指示器实现
    if (this.container.querySelector('#scroll-loader-loading')) return;

    const loadingEl = document.createElement('div');
    loadingEl.className = 'scroll-loader-loading';
    loadingEl.id = 'scroll-loader-loading';
    loadingEl.innerHTML = `
      <div class="loading-spinner" style="
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        color: #666;
        font-size: 14px;
      ">
        <div style="
          width: 16px;
          height: 16px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #666;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 8px;
        "></div>
        
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    this.container.appendChild(loadingEl);
  }

  /**
   * 隐藏加载指示器
   * 内置默认实现，也支持通过 config.loadingUI 覆盖
   */
  hideLoading() {
    // 如果提供了自定义加载UI，则使用自定义实现
    if (this.config.loadingUI && typeof this.config.loadingUI.hide === 'function') {
      this.config.loadingUI.hide();
      return;
    }

    // 默认加载指示器实现
    const loadingEl = this.container.querySelector('#scroll-loader-loading');
    if (loadingEl) {
      loadingEl.remove();
    }
  }

  /**
   * 显示错误信息
   * 使用全局 Toast 组件显示错误信息，也支持通过 config.errorUI 覆盖
   * @param {String} message 错误信息
   */
  showErrorMessage(message) {
    // 如果提供了自定义错误UI，则使用自定义实现
    if (this.config.errorUI && typeof this.config.errorUI.show === 'function') {
      this.config.errorUI.show(message);
      return;
    }

    // 使用全局 Toast 组件显示错误信息
    pushErrorMessage(message || '加载失败，请稍后重试');
  }

  /**
   * 加载数据
   * @returns {Promise}
   */
  async load() {
    if (this.isLoading) {
      return;
    }

    if (!this.hasMore) {
      return;
    }

    // 检查缓存
    const cacheKey = `${this.config.cacheKeyPrefix}_${this.page}`;
    if (this.cache.has(cacheKey)) {
      const cachedData = this.cache.get(cacheKey);
      this.renderData(cachedData.list, cachedData.total);

      // 判断是否还有更多数据
      // 根据当前页返回的数据量是否小于pageSize来判断是否还有更多数据
      const currentPageCount = cachedData.list.length;

      // 如果当前页返回的数据量小于pageSize，则表示没有更多数据了
      if (currentPageCount < this.config.pageSize) {
        this.hasMore = false;
      } else {
        this.page++; // 准备加载下一页

        // 检查容器是否可滚动
        this.checkScrollableAfterRender();
      }
      return;
    }

    this.isLoading = true;
    this.showLoading();

    try {
      // 调用加载数据函数
      const result = await this.loadData({
        page: this.page,
        pageSize: this.config.pageSize
      });

      // 处理返回结果
      const list = result?.list || [];
      const total = result?.total || 0;

      // 缓存数据
      this.cache.set(cacheKey, { list, total });

      // 渲染数据
      this.renderData(list, total);

      // 判断是否还有更多数据
      // 根据当前页返回的数据量是否小于pageSize来判断是否还有更多数据
      const currentPageCount = list.length;

      // 如果当前页返回的数据量小于pageSize，则表示没有更多数据了
      if (currentPageCount < this.config.pageSize) {
        this.hasMore = false;
      } else {
        this.page++; // 准备加载下一页

        // 检查容器是否可滚动
        this.checkScrollableAfterRender();
      }

    } catch (error) {
      // 调用错误处理回调
      if (this.config.onError && typeof this.config.onError === 'function') {
        this.config.onError(error);
      } else {
        this.showErrorMessage('加载数据失败，请稍后重试');
      }
    } finally {
      this.hideLoading();
      this.isLoading = false;
    }
  }

  /**
   * 检查渲染后容器是否可滚动
   */
  checkScrollableAfterRender() {
    setTimeout(() => {
      const { scrollHeight, clientHeight } = this.container;

      if (scrollHeight <= clientHeight && this.hasMore) {
        this.load(); // 如果容器未满且还有更多数据，自动加载下一页
      }
    }, 100); // 短暂延迟确保DOM已更新
  }

  /**
   * 清除缓存
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * 重新加载
   */
  reload() {
    this.page = 1;
    this.hasMore = true;
    this.clearCache();

    // 如果提供了清空容器的回调，则调用
    if (this.config.onClear && typeof this.config.onClear === 'function') {
      this.config.onClear();
    }

    this.load();
  }

  /**
   * 清理资源
   */
  destroy() {
    this.container.removeEventListener('scroll', this.onScroll);
    this.clearCache();

    // 清理加载UI元素
    this.hideLoading();
    // 不需要清理错误UI元素，因为使用的是Toast组件
  }
}

export default ScrollLoader;
